import { authApiClient } from '@/services/axios'
import type { NotificationFromStrapi, NotificationResponse } from '@/types/strapiContentTypes'

export interface DeviceTokenData {
  token_id: string
  token_type?: 'Subscription' | 'OneSignal'
  device_type: 'android' | 'ios' | 'chrome' | 'firefox' | 'safari' | 'edge' | 'windows' | 'linux' | 'macos' | 'other'
  device_name: string
  os_version: string
}

export interface DeviceTokenRequest {
  data: DeviceTokenData
}

class NotificationService {
  async registerDeviceToken(deviceData: DeviceTokenData): Promise<void> {
    // Skip token registration in development mode
    /* if (import.meta.env.DEV) {
      console.log('📱 Device token registration skipped in development mode')
      return
    } */

    try {
      const payload: DeviceTokenRequest = {
        data: deviceData
      }
      
      console.log('Registering device token with payload:', payload)
      
      const response = await authApiClient.post('/notification-tokens', payload)
      
      console.log('Device token registration response:', response.data)
    } catch (error: unknown) {
      const apiError = error as { message: string; response?: { data?: unknown; status?: number } }
      console.error('Failed to register device token:', {
        error: apiError.message,
        response: apiError.response?.data,
        status: apiError.response?.status,
        payload: deviceData
      })
      const errorData = apiError.response?.data as { error?: { message?: string } } | undefined
      throw new Error(`Device token registration failed: ${errorData?.error?.message || apiError.message}`)
    }
  }

  async unregisterDeviceToken(tokenId: string): Promise<void> {
    try {
      console.log('Unregistering device token with ID:', tokenId)
      
      const response = await authApiClient.delete(`/notification-tokens/${tokenId}`)
      
      console.log('Device token unregistration response:', response.data)
    } catch (error: unknown) {
      const apiError = error as { message: string; response?: { data?: unknown; status?: number } }
      console.error('Failed to unregister device token:', {
        error: apiError.message,
        response: apiError.response?.data,
        status: apiError.response?.status,
        tokenId
      })
      const errorData = apiError.response?.data as { error?: { message?: string } } | undefined
      throw new Error(`Device token unregistration failed: ${errorData?.error?.message || apiError.message}`)
    }
  }

  getDeviceInfo(): Omit<DeviceTokenData, 'token_id' | 'token_type'> {
    const userAgent = navigator.userAgent
    let deviceName = 'Unknown Browser'
    let deviceType: DeviceTokenData['device_type'] = 'other'
    let osVersion = 'Unknown'

    // Detect browser and set appropriate device_type
    if (userAgent.includes('Edg/')) {
      deviceName = 'Microsoft Edge'
      deviceType = 'edge'
    } else if (userAgent.includes('Chrome/')) {
      deviceName = 'Google Chrome'
      deviceType = 'chrome'
    } else if (userAgent.includes('Firefox/')) {
      deviceName = 'Mozilla Firefox'
      deviceType = 'firefox'
    } else if (userAgent.includes('Safari/') && !userAgent.includes('Chrome')) {
      deviceName = 'Safari'
      deviceType = 'safari'
    }

    // Extract OS version and potentially override device_type for OS-specific classification
    if (userAgent.includes('Windows NT')) {
      const match = userAgent.match(/Windows NT (\d+\.\d+)/)
      osVersion = match ? `Windows ${match[1]}` : 'Windows'
      // Keep browser-specific device_type for Windows
    } else if (userAgent.includes('Mac OS X')) {
      const match = userAgent.match(/Mac OS X (\d+_\d+_\d+)/)
      osVersion = match && match[1] ? `macOS ${match[1].replace(/_/g, '.')}` : 'macOS'
      // Keep browser-specific device_type for macOS
    } else if (userAgent.includes('Linux')) {
      osVersion = 'Linux'
      // Keep browser-specific device_type for Linux
    } else if (userAgent.includes('Android')) {
      osVersion = 'Android'
      deviceType = 'android'
    } else if (userAgent.includes('iPhone') || userAgent.includes('iPad')) {
      osVersion = 'iOS'
      deviceType = 'ios'
    }

    return {
      device_type: deviceType,
      device_name: deviceName,
      os_version: osVersion
    }
  }

  /**
   * Fetch recent notifications for the current user
   * @param limit Number of notifications to fetch (default 10)
   */
  async getNotifications(limit: number = 10): Promise<NotificationFromStrapi[]> {
    try {
      const params = new URLSearchParams({
        'pagination[limit]': limit.toString(),
        'sort[0]': 'createdAt:desc',
      })

      const response = await authApiClient.get<NotificationResponse>(`/notifications?${params.toString()}`)
      return response.data.data
    } catch (error) {
      console.error('Failed to fetch notifications:', error)
      return []
    }
  }

  /**
   * Mark a notification as read
   * @param documentId The document ID of the notification
   */
  async markAsRead(documentId: string): Promise<void> {
    try {
      await authApiClient.put(`/notifications/${documentId}`, {
        data: {
          read: true
        }
      })
    } catch (error) {
      console.error(`Failed to mark notification ${documentId} as read:`, error)
      throw error
    }
  }

  /**
   * Mark all unread notifications as read
   */
  async markAllAsRead(documentIds: string[]): Promise<void> {
    try {
      // Strapi v5 doesn't have a bulk update by default, so we might need to do this one by one or use a custom endpoint.
      // For now, we'll do it in parallel.
      await Promise.all(documentIds.map(id => this.markAsRead(id)))
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error)
      throw error
    }
  }
}

export const notificationService = new NotificationService()
